import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { setAnalyticsUser, trackPageView } from './lib/analytics';

import AuthPage from './pages/auth/AuthPage';
import InstallPrompt from './components/InstallPrompt';
import { isAdminPortalHost, isAgentPortalHost } from './lib/adminPortal';

const GoogleAuthCallbackPage = lazy(() => import('./pages/auth/GoogleAuthCallbackPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const PlayerApp = lazy(() => import('./pages/player/PlayerApp'));
const RoulettePage = lazy(() => import('./pages/player/RoulettePage'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const StaffPortalPage = lazy(() => import('./pages/staff/StaffPortalPage'));
const FinanceAuthPage = lazy(() => import('./pages/finance/FinanceAuthPage'));
const FinanceDashboard = lazy(() => import('./pages/finance/FinanceDashboard'));
const DepositReturnPage = lazy(() => import('./pages/player/DepositReturnPage'));
const ReferralStaffDashboard = lazy(() => import('./pages/ReferralStaffDashboard'));
const PrivacyPolicyPage = lazy(() => import('./pages/legal/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/legal/TermsOfServicePage'));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0E14' }}>
      <div className="spinner" />
    </div>
  );
}

function ProtectedRoute({ children, requiredRole }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0E14' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  if (requiredRole === 'admin' && !['admin', 'moderator', 'support'].includes(user.role)) {
    if (user.role === 'referral_agent') {
      return <Navigate to="/referral-staff" replace />;
    }
    return <Navigate to="/app" replace />;
  }

  return children;
}

/**
 * Restricts a route to users whose role is `referral_agent`. Other
 * roles are bounced to the place that actually makes sense for them.
 */
function ReferralRoleGuard({ children }) {
  const { user } = useAuth();
  if (!user) return children;   // ProtectedRoute already handles unauthed state
  if (user.role === 'referral_agent') return children;
  if (['admin', 'moderator', 'support'].includes(user.role)) {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to="/app" replace />;
}

function AppRouter() {
  const adminPortal = isAdminPortalHost();
  const agentPortal = isAgentPortalHost();
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/callback" element={<Navigate to="/auth" replace />} />
        <Route path="/auth/google" element={<GoogleAuthCallbackPage />} />
        <Route path="/app" element={<ProtectedRoute><PlayerApp /></ProtectedRoute>} />
        <Route path="/app/roulette" element={<ProtectedRoute><RoulettePage /></ProtectedRoute>} />
        <Route path="/app/explore/roulette" element={<ProtectedRoute><RoulettePage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
        <Route path="/admin/*" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
        <Route path="/staff-portal" element={<ProtectedRoute><StaffPortalPage /></ProtectedRoute>} />

        <Route path="/referral-staff" element={<ProtectedRoute><ReferralRoleGuard><ReferralStaffDashboard /></ReferralRoleGuard></ProtectedRoute>} />

        <Route path="/deposit-return" element={<DepositReturnPage />} />

        <Route path="/finance/auth" element={<FinanceAuthPage />} />
        <Route path="/finance" element={<FinanceDashboard />} />

        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />

        <Route path="/" element={adminPortal ? <Navigate to="/admin" replace /> : agentPortal ? <Navigate to="/referral-staff" replace /> : <LandingPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function GoogleAnalyticsRouteTracker() {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    setAnalyticsUser(user, null);
  }, [user]);

  useEffect(() => {
    if (/^\/([a-z0-9-]+\/)?app(\/|$)/i.test(location.pathname) || location.pathname === '/' || location.pathname === '/auth') {
      document.title = 'Juwa.US';
    }
    trackPageView(`${location.pathname}${location.search}`, document.title);
  }, [location.pathname, location.search]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <GoogleAnalyticsRouteTracker />
          <AppRouter />
          <InstallPrompt />
          <Toaster
            position="top-right"
            richColors
            toastOptions={{
              style: {
                background: '#131822',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff'
              }
            }}
          />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
